/**
 * Мета-описания полей сущностей (задача/проект) и схемы шаблонов.
 *
 * Единственный источник истины для набора полей, подписей, типов,
 * requiredInCreate и UI-конфигурации. Потребители:
 *   - формы создания (TaskFormTab, ProjectModal) — читают label/type/
 *     requiredInCreate/options из ENTITY_FIELDS_META (полный набор);
 *   - карточка шаблона (TemplateModal, TaskNodeFields) — читают
 *     TEMPLATE_FIELDS_META (только поля без templateExcluded);
 *   - санитайзеры — выводят TASK_FIELDS/PROJECT_FIELDS/NODE_TASK_FIELDS
 *     из ENTITY_FIELDS_META, исключая templateExcluded.
 *
 * Инварианты:
 *   - Все поля шаблона опциональны: requiredInCreate используется только
 *     формой создания.
 *   - Поля с templateExcluded не попадают в шаблон и в его санитайзеры,
 *     но остаются в форме создания.
 *   - isSummary и status в шаблон не входят (Q1, Q3).
 *   - projectId у вложенного узла наследуется от родителя и в payload
 *     поддерева не сохраняется.
 *   - Метки хранятся в payload как tags (массив), в форме — как
 *     tagsInput (строка). Отображение задаётся formField.
 *   - Селекты в шаблоне используют те же источники опций, что и формы
 *     создания: activeProjects — db.projects (active, !archived),
 *     assignableEmployees — useAssignableEmployeesQuery, activeEmployees —
 *     db.employees (не уволенные).
 */
import { PROJECT_TYPES } from "./constants";
import { sanitizeTagsArray } from "./tags";

const MAX_STR = 500;
const MAX_DEPTH = 5;
const MAX_NODES = 100;

const TASK_PRIORITIES = ["low", "mid", "high", "crit"];
const DEPENDENCY_TYPES = ["FS", "SS", "FF"];
const PROJECT_PRIORITIES = ["AOG", "CRIT", "NORM", "high", "mid", "low"];

const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
const TIME_RE = /^\d{2}:\d{2}$/;

const trimmedString = (value, max = MAX_STR) => {
  if (typeof value !== "string") return "";
  const trimmed = value.trim();
  return trimmed ? trimmed.slice(0, max) : "";
};

const nonNegativeNumber = (value) => {
  if (value === "" || value === null || value === undefined) return null;
  const n = Number(value);
  return Number.isFinite(n) && n >= 0 ? n : null;
};

const sanitizedDate = (value) => {
  if (typeof value !== "string") return null;
  const t = value.trim();
  return DATE_RE.test(t) ? t : null;
};

const sanitizedTime = (value) => {
  if (typeof value !== "string") return null;
  const t = value.trim();
  return TIME_RE.test(t) ? t : null;
};

const sanitizedBoolean = (value) => (typeof value === "boolean" ? value : null);

const sanitizedStringArray = (value) => {
  if (!Array.isArray(value)) return null;
  const out = value
    .filter((v) => typeof v === "string" && v.trim())
    .map((v) => v.trim());
  return out.length > 0 ? out : null;
};

const enumValue = (allowed) => (value) =>
  allowed.includes(value) ? value : null;

const PRIORITY_OPTIONS = Object.freeze([
  { value: "low", label: "Низкий" },
  { value: "mid", label: "Средний" },
  { value: "high", label: "Высокий" },
  { value: "crit", label: "Критический" },
]);

const DEPENDENCY_TYPE_OPTIONS = Object.freeze([
  { value: "FS", label: "Окончание-Начало (FS)" },
  { value: "SS", label: "Начало-Начало (SS)" },
  { value: "FF", label: "Окончание-Окончание (FF)" },
]);

const PROJECT_PTYPE_OPTIONS = Object.freeze([
  { value: "prod", label: "Производственный" },
  { value: "admin", label: "Административный" },
]);

export const ENTITY_FIELDS_META = Object.freeze({
  task: Object.freeze({
    title: {
      label: "Название",
      type: "text",
      requiredInCreate: true,
      sanitize: trimmedString,
    },
    desc: { label: "Описание", type: "textarea", sanitize: trimmedString },
    tags: {
      label: "Метки",
      type: "tags",
      formField: "tagsInput",
      sanitize: sanitizeTagsArray,
    },
    projectId: {
      label: "Проект",
      type: "select",
      requiredInCreate: true,
      optionsSource: "activeProjects",
      sanitize: trimmedString,
    },
    assigneeId: {
      label: "Исполнитель",
      type: "select",
      requiredInCreate: true,
      optionsSource: "assignableEmployees",
      sanitize: trimmedString,
    },
    priority: {
      label: "Приоритет",
      type: "select",
      requiredInCreate: true,
      options: PRIORITY_OPTIONS,
      sanitize: enumValue(TASK_PRIORITIES),
    },
    plannedHours: {
      label: "Плановые часы",
      type: "number",
      requiredInCreate: true,
      sanitize: nonNegativeNumber,
    },
    start: {
      label: "Начало",
      type: "date",
      requiredInCreate: true,
      templateExcluded: true,
      sanitize: sanitizedDate,
    },
    deadline: {
      label: "Срок исполнения",
      type: "date",
      requiredInCreate: true,
      templateExcluded: true,
      sanitize: sanitizedDate,
    },
    isHourly: {
      label: "Часовая задача",
      type: "toggle",
      templateExcluded: true,
      sanitize: sanitizedBoolean,
    },
    startTime: {
      label: "Время начала",
      type: "time",
      requiredInCreate: true,
      templateExcluded: true,
      sanitize: sanitizedTime,
    },
    endTime: {
      label: "Время окончания",
      type: "time",
      requiredInCreate: true,
      templateExcluded: true,
      sanitize: sanitizedTime,
    },
    dependencyId: {
      label: "Зависит от задачи",
      type: "select",
      optionsSource: "projectTasks",
      templateExcluded: true,
      sanitize: trimmedString,
    },
    dependencyType: {
      label: "Тип зависимости",
      type: "select",
      options: DEPENDENCY_TYPE_OPTIONS,
      templateExcluded: true,
      sanitize: enumValue(DEPENDENCY_TYPES),
    },
  }),
  project: Object.freeze({
    name: {
      label: "Название",
      type: "text",
      requiredInCreate: true,
      sanitize: trimmedString,
    },
    desc: { label: "Описание", type: "textarea", sanitize: trimmedString },
    tags: {
      label: "Метки",
      type: "tags",
      formField: "tagsInput",
      sanitize: sanitizeTagsArray,
    },
    customer: {
      label: "Заказчик",
      type: "text",
      requiredInCreate: true,
      sanitize: trimmedString,
    },
    ptype: {
      label: "Тип проекта",
      type: "select",
      requiredInCreate: true,
      options: PROJECT_PTYPE_OPTIONS,
      sanitize: enumValue(Object.keys(PROJECT_TYPES)),
    },
    aircraftType: {
      label: "Тип ВС",
      type: "text",
      sanitize: trimmedString,
    },
    projectType: {
      label: "Категория",
      type: "text",
      sanitize: trimmedString,
    },
    priority: {
      label: "Приоритет",
      type: "priority",
      requiredInCreate: true,
      sanitize: enumValue(PROJECT_PRIORITIES),
    },
    unitIds: {
      label: "Подразделения",
      type: "multiselect",
      requiredInCreate: true,
      optionsSource: "units",
      sanitize: sanitizedStringArray,
    },
    plannedHours: {
      label: "Плановые часы, ч",
      type: "number",
      requiredInCreate: true,
      sanitize: nonNegativeNumber,
    },
    managerId: {
      label: "Ответственный",
      type: "select",
      optionsSource: "activeEmployees",
      sanitize: trimmedString,
    },
    longterm: {
      label: "Долгосрочный",
      type: "toggle",
      sanitize: sanitizedBoolean,
    },
    code: {
      label: "Код",
      type: "text",
      requiredInCreate: true,
      templateExcluded: true,
      sanitize: trimmedString,
    },
    start: {
      label: "Начало",
      type: "date",
      requiredInCreate: true,
      templateExcluded: true,
      sanitize: sanitizedDate,
    },
    end: {
      label: "Окончание",
      type: "date",
      templateExcluded: true,
      sanitize: sanitizedDate,
    },
    contractNumber: {
      label: "Номер договора",
      type: "text",
      economics: true,
      templateExcluded: true,
      sanitize: trimmedString,
    },
    hourlyRate: {
      label: "Ставка ₽/ч",
      type: "number",
      economics: true,
      templateExcluded: true,
      sanitize: nonNegativeNumber,
    },
  }),
});

const deriveSchema = (meta) =>
  Object.freeze(
    Object.fromEntries(
      Object.entries(meta)
        .filter(([, v]) => !v.templateExcluded)
        .map(([k, v]) => [k, v.sanitize]),
    ),
  );

const deriveUi = (meta) =>
  Object.freeze(
    Object.fromEntries(
      Object.entries(meta)
        .filter(([, v]) => !v.templateExcluded)
        .map(([k, entry]) => {
          const ui = { ...entry };
          delete ui.sanitize;
          return [k, Object.freeze(ui)];
        }),
    ),
  );

export const TASK_FIELDS = deriveSchema(ENTITY_FIELDS_META.task);
export const PROJECT_FIELDS = deriveSchema(ENTITY_FIELDS_META.project);

export const NODE_TASK_FIELDS = Object.freeze(
  Object.fromEntries(
    Object.entries(TASK_FIELDS).filter(([k]) => k !== "projectId"),
  ),
);

export const TEMPLATE_FIELDS_META = Object.freeze({
  task: deriveUi(ENTITY_FIELDS_META.task),
  project: deriveUi(ENTITY_FIELDS_META.project),
});

const sanitizeBySchema = (schema, source) => {
  const result = {};
  for (const field of Object.keys(schema)) {
    const sanitized = schema[field](source[field]);
    if (sanitized !== null && sanitized !== "") result[field] = sanitized;
  }
  return result;
};

const sanitizeTaskTree = (source, depth, limits) => {
  if (depth > MAX_DEPTH || limits.remaining <= 0) return null;
  if (!source || typeof source !== "object") return null;
  limits.remaining -= 1;

  const node = sanitizeBySchema(NODE_TASK_FIELDS, source);

  const rawChildren = Array.isArray(source.subtasks) ? source.subtasks : [];
  const children = [];
  for (const child of rawChildren) {
    const clean = sanitizeTaskTree(child, depth + 1, limits);
    if (clean) children.push(clean);
  }
  if (children.length) node.subtasks = children;

  return Object.keys(node).length ? node : null;
};

const sanitizeTaskList = (source, limits) => {
  if (!Array.isArray(source)) return [];
  const list = [];
  for (const item of source) {
    const clean = sanitizeTaskTree(item, 1, limits);
    if (clean) list.push(clean);
  }
  return list;
};

export const TEMPLATE_KINDS = Object.freeze({
  task: Object.freeze({
    label: "задачи",
    nestedKey: "subtasks",
    nestedLabel: "подзадачи",
    nestedSingular: "подзадача",
  }),
  project: Object.freeze({
    label: "проекта",
    nestedKey: "tasks",
    nestedLabel: "задачи",
    nestedSingular: "задача",
  }),
});

export const TEMPLATE_KIND_LABELS = Object.freeze({
  task: "задача",
  project: "проект",
});

export const isValidTemplateKind = (kind) =>
  Object.prototype.hasOwnProperty.call(TEMPLATE_KINDS, kind);

export function extractTemplatePayload(kind, source) {
  if (!isValidTemplateKind(kind) || !source || typeof source !== "object")
    return null;
  const limits = { remaining: MAX_NODES };

  if (kind === "task") {
    const payload = sanitizeBySchema(TASK_FIELDS, source);
    if (Array.isArray(source.subtasks)) {
      const subtasks = sanitizeTaskList(source.subtasks, limits);
      if (subtasks.length) payload.subtasks = subtasks;
    }
    return Object.keys(payload).length ? payload : null;
  }

  const payload = sanitizeBySchema(PROJECT_FIELDS, source);
  if (Array.isArray(source.tasks)) {
    const tasks = sanitizeTaskList(source.tasks, limits);
    if (tasks.length) payload.tasks = tasks;
  }
  return Object.keys(payload).length ? payload : null;
}

export function applyTemplatePayload(kind, payload) {
  if (!isValidTemplateKind(kind) || !payload || typeof payload !== "object")
    return {};
  const limits = { remaining: MAX_NODES };

  if (kind === "task") {
    const result = sanitizeBySchema(TASK_FIELDS, payload);
    if (Array.isArray(payload.subtasks)) {
      const subtasks = sanitizeTaskList(payload.subtasks, limits);
      if (subtasks.length) result.subtasks = subtasks;
    }
    return result;
  }

  const result = sanitizeBySchema(PROJECT_FIELDS, payload);
  if (Array.isArray(payload.tasks)) {
    const tasks = sanitizeTaskList(payload.tasks, limits);
    if (tasks.length) result.tasks = tasks;
  }
  return result;
}
